import { useEffect, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { Calculator, MapPin, LineChart, FileText, Loader2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { AuthDialog } from "@/components/site/AuthDialog";
import { EstimadorWizard } from "@/components/estimador/EstimadorWizard";
import { supabase } from "@/integrations/supabase/client";

export const Route = createFileRoute("/estimador")({
  head: () => ({
    meta: [
      { title: "Valorador de valor de tu negocio — Bizaclia" },
      {
        name: "description",
        content:
          "Estima gratis el valor de traspaso, venta o alquiler de tu local o negocio con datos reales del mercado. Resultado en minutos.",
      },
      { property: "og:title", content: "Valorador de valor de tu negocio — Bizaclia" },
      {
        property: "og:description",
        content:
          "Calcula el valor de tu local o negocio con datos reales del mercado. Inicia sesión y obtén tu valoración.",
      },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "/estimador" }],
  }),
  component: EstimadorPublicoPage,
});

const PASOS = [
  {
    icon: MapPin,
    title: "Cuéntanos dónde está",
    text: "Tipo de operación y ubicación exacta del local, con búsqueda de dirección en el mapa.",
  },
  {
    icon: Calculator,
    title: "Describe el local y el negocio",
    text: "Superficie, estado, renta mensual, facturación o EBITDA: cuantos más datos, más precisa la valoración.",
  },
  {
    icon: LineChart,
    title: "Recibe tu valoración",
    text: "Un rango de valor calculado con comparables reales publicados en Bizaclia.",
  },
  {
    icon: FileText,
    title: "Descarga el informe",
    text: "Genera un informe profesional de valoración listo para compartir o imprimir.",
  },
];

function EstimadorPublicoPage() {
  const [authed, setAuthed] = useState<boolean | null>(null);
  const [openAuth, setOpenAuth] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setAuthed(!!data.session));
    const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
      setAuthed(!!session);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const comenzar = () => {
    if (authed) setStarted(true);
    else setOpenAuth(true);
  };

  if (started && authed) {
    return (
      <main className="mx-auto max-w-5xl px-4 py-10">
        <header className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight">Valorador de valor</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Completa los pasos para obtener una valoración del valor de tu negocio. No se publica
            ningún anuncio.
          </p>
        </header>
        <EstimadorWizard />
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-5xl px-4 py-12">
      <header className="max-w-2xl">
        <h1 className="text-4xl font-bold tracking-tight">
          ¿Cuánto vale tu local o negocio?
        </h1>
        <p className="mt-3 text-lg text-muted-foreground">
          Nuestro valorador cruza los datos de tu local con anuncios reales del mercado para darte
          un rango de valor orientativo de traspaso, venta o alquiler en pocos minutos.
        </p>
        <div className="mt-6 flex flex-wrap items-center gap-3">
          <Button size="lg" onClick={comenzar} disabled={authed === null}>
            {authed === null ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Cargando…
              </>
            ) : (
              "Comenzar valoración"
            )}
          </Button>
          {authed === false && (
            <span className="text-sm text-muted-foreground">
              Necesitas iniciar sesión para usar el valorador.
            </span>
          )}
        </div>
      </header>

      <section className="mt-12 grid gap-4 sm:grid-cols-2">
        {PASOS.map((p) => (
          <Card key={p.title}>
            <CardContent className="flex gap-4 p-5">
              <span className="mt-0.5 text-primary">
                <p.icon className="h-5 w-5" />
              </span>
              <span>
                <span className="block text-sm font-semibold">{p.title}</span>
                <span className="mt-1 block text-sm text-muted-foreground">{p.text}</span>
              </span>
            </CardContent>
          </Card>
        ))}
      </section>

      <p className="mt-10 max-w-2xl text-xs text-muted-foreground">
        La valoración es orientativa y no constituye una tasación oficial. Su precisión depende de
        los datos aportados y de los comparables disponibles en la zona.
      </p>

      <AuthDialog
        open={openAuth}
        onOpenChange={setOpenAuth}
        onComplete={() => {
          setOpenAuth(false);
          setStarted(true);
        }}
      />
    </main>
  );
}
